// src/utils/thuNhoAnh.js
//
// ============================================================
// 📷 THU NHỎ ẢNH NGAY TRÊN ĐIỆN THOẠI, TRƯỚC KHI GỬI (Đợt N5, 14/09/2026)
// ------------------------------------------------------------
// Chân dung 5.10: ảnh thu nhỏ ở trình duyệt, ~300 KB. Lý do hiện hành (lộ
// trình N5 đã sửa lý do cũ về RAM): đỡ tốn 4G của người gửi, và máy chủ khỏi
// cần thư viện xử lý ảnh.
//
// Anh đồng ý 14/09: CẠNH DÀI 1600 điểm ảnh — đủ đọc chữ trên hoá đơn khi
// phóng to. Ảnh điện thoại gốc ~4000 px, 3–5 MB.
//
// ⚠️ ẢNH XOAY NGANG: ảnh chụp dọc trên điện thoại thường lưu NGANG kèm một
// cờ "hãy xoay 90°". `createImageBitmap(..., {imageOrientation: "from-image"})`
// đọc cờ đó. Bỏ tùy chọn này là hoá đơn dọc lên app thành nằm ngang.
//
// ⚠️ ẢNH HEIC CỦA IPHONE: Safari trên iPhone tự đổi sang JPEG khi chọn ảnh qua
// ô chọn tệp, nên thường không vấp. Máy tính Windows mở tệp .heic thì trình
// duyệt không đọc được → báo câu dễ hiểu thay vì lỗi kỹ thuật.
// ============================================================

export const CANH_DAI_TOI_DA = 1600;
const CHAT_LUONG_DAU = 0.82;
const TRAN_BYTE = 600 * 1024; // vượt thì hạ chất lượng thêm; máy chủ chặn ở 2 MB

/**
 * Kích thước sau khi thu nhỏ, giữ nguyên tỉ lệ. Ảnh đã nhỏ hơn thì để nguyên.
 * tinhKichThuoc(4000, 3000) → { rong: 1600, cao: 1200 }
 */
export function tinhKichThuoc(rong, cao, toiDa = CANH_DAI_TOI_DA) {
  if (!(rong > 0) || !(cao > 0)) return { rong: 0, cao: 0 };
  const canhDai = Math.max(rong, cao);
  if (canhDai <= toiDa) return { rong, cao };
  const tiLe = toiDa / canhDai;
  return { rong: Math.round(rong * tiLe), cao: Math.round(cao * tiLe) };
}

/** "3,4 MB" · "290 KB" — để hiện "đã thu nhỏ 3,4 MB → 290 KB" */
export function veDungLuong(soByte) {
  if (!(soByte >= 0)) return "";
  if (soByte >= 1024 * 1024) {
    return `${(soByte / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
  }
  return `${Math.max(1, Math.round(soByte / 1024))} KB`;
}

const veRaBlob = (canvas, chatLuong) =>
  new Promise((xong) => canvas.toBlob(xong, "image/jpeg", chatLuong));

/**
 * Nhận tệp người dùng chọn, trả về ảnh JPEG đã thu nhỏ.
 * @param {File} tep
 * @returns {Promise<Blob>}
 */
export async function thuNhoAnh(tep) {
  let anh;
  try {
    anh = await createImageBitmap(tep, { imageOrientation: "from-image" });
  } catch (e) {
    const loi = new Error(
      "Không đọc được ảnh này. Nếu là ảnh .HEIC của iPhone, hãy chọn ảnh từ điện thoại hoặc đổi sang JPG.",
    );
    loi.nguoiDung = true;
    throw loi;
  }
  const { rong, cao } = tinhKichThuoc(anh.width, anh.height);
  const canvas = document.createElement("canvas");
  canvas.width = rong;
  canvas.height = cao;
  const ve = canvas.getContext("2d");
  // Nền trắng: ảnh PNG có vùng trong suốt mà đổi sang JPEG thì vùng đó thành đen.
  ve.fillStyle = "#fff";
  ve.fillRect(0, 0, rong, cao);
  ve.drawImage(anh, 0, 0, rong, cao);
  if (anh.close) anh.close();

  let chatLuong = CHAT_LUONG_DAU;
  let blob = await veRaBlob(canvas, chatLuong);
  while (blob && blob.size > TRAN_BYTE && chatLuong > 0.5) {
    chatLuong -= 0.1;
    blob = await veRaBlob(canvas, chatLuong);
  }
  if (!blob) throw new Error("Không thu nhỏ được ảnh");
  return blob;
}
